import ICarouselContentDTO from "../../Models/DTOs/ICarouselContentDTO";
import { IFavoriteItemDTO } from "../../Models/DTOs/IFavoritesDTO";

import { ContentType } from "../../Models/Enums/ContentType";
import { ContentAccessType } from "../../Models/Enums/ContentAccessType";

type LinkBuilders = {
    seriesLink?: (seriesId: number) => string;
    videoLink?: (videoId: number) => string;
};

// Default: change these to match your real routing
const defaultLinks: Required<LinkBuilders> = {
    seriesLink: (id) => `/series/${id}`,
    videoLink: (id) => `/video/${id}`
};

function safeIso(d?: string | null): string {
    return d ?? "";
}

function safeStr(s?: string | null): string {
    return s ?? "";
}

function safeNum(n?: number | null, fallback = 0): number {
    return typeof n === "number" ? n : fallback;
}

// Note: AccessLevel in your payload is numeric. If your ContentAccessType matches those numeric values, this is perfect.
// If it doesn't, replace this with a mapping.
function mapAccessLevel(accessLevel: any): ContentAccessType {
    return accessLevel as ContentAccessType;
}

export function FavoriteItemToCarouselContentDTO(
    fav: IFavoriteItemDTO,
    links: LinkBuilders = defaultLinks
): ICarouselContentDTO {
    const seriesLink = links.seriesLink ?? defaultLinks.seriesLink;
    const videoLink = links.videoLink ?? defaultLinks.videoLink;

    if (fav.ContentType === "series") {
        const s = fav.Series;

        return {
            Id: safeNum(s.Id),
            Title: safeStr(s.Title),
            Text: safeStr(s.Description),
            Season: 0,
            Episode: 0,
            CategoryId: safeNum(s.CategoryId),
            Thumbnail: safeStr(s.HeaderImg) || safeStr(s.SeriesCover),
            PortraitThumbnail: safeStr(s.SeriesCover) || safeStr(s.HeaderImg),
            BackgroundImage: safeStr(s.BackgroundImage) || safeStr(s.HeaderImg),

            AccessType: mapAccessLevel(s.AccessLevel),
            ContentType: ContentType.Series as any, // swap to exact enum value your app uses

            // These names are a bit "carousel-y" but we need something consistent:
            Created: safeIso(fav.AddedDate),
            ReleaseDate: safeIso(s.Datetime),
            ReleaseDateEarlyAccess: "",

            IsOriginal: !!s.IsOriginal,
            IsFreeView: false,
            IsFreeViewApp: false,
            IsVisible: true,
            IsFavourite: true,
            Rating: 0,
            IsBookable: false,
            IsClickeable: true,

            // Series doesn’t have a playable link here; this is for navigation
            VideoLinkHLS: "",
            VideoLinkDash: "",
            TrailerLinkHLS: safeStr(s.TrailerLink),
            TrailerLinkDash: "",

            Link: seriesLink(s.Id),
            OptionalLabel: "",
            Authors: undefined
        };
    }

    // video-single
    const v = fav.Video;

    return {
        Id: safeNum(v.Id),
        Title: safeStr(v.Title),
        Text: safeStr(v.Description),
        Season: safeNum(v.Season),
        Episode: safeNum(v.Episode),
        CategoryId: safeNum(v.VideoCategoriesId),
        Thumbnail: safeStr(v.Thumbnail),
        PortraitThumbnail: safeStr(v.PortraitThumbnail) || safeStr(v.Thumbnail),
        BackgroundImage: safeStr(v.BackgroundImage) || safeStr(v.Thumbnail),

        AccessType: mapAccessLevel(v.AccessLevel),
        ContentType: ContentType.Film as any, // swap to exact enum value your app uses

        Created: safeIso(fav.AddedDate),
        ReleaseDate: safeIso(v.Datetime),
        ReleaseDateEarlyAccess: safeIso(v.DatetimeEarlyAccess),

        IsOriginal: !!v.IsOriginal,
        IsFreeView: !!v.IsFreeView,
        IsFreeViewApp: !!v.IsFreeViewApp,
        IsVisible: true,
        IsFavourite: true,
        Rating: 0,
        IsBookable: false,
        IsClickeable: true,

        VideoLinkHLS: safeStr(v.Link),
        VideoLinkDash: "",
        TrailerLinkHLS: safeStr(v.TrailerLink),
        TrailerLinkDash: "",

        Link: videoLink(v.Id),
        OptionalLabel: "",
        Authors: v.Authors ?? undefined
    };
}

export function FavoritesToCarouselItems(
    items: IFavoriteItemDTO[] | undefined | null,
    links?: LinkBuilders
): ICarouselContentDTO[] {
    if (!items || items.length === 0) return [];
    return items.map((x) => FavoriteItemToCarouselContentDTO(x, links));
}
